// codex was here!
import React, { useState, useEffect } from "react";
import "./App.css";
import logo from "./new-logo.jpg"; 

//const API_BASE = "http://localhost:8000";
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Component to handle authenticated image loading
function AuthImage({ src, alt, className, onClick, bgColor = 'black' }) {
  // Simplified: just use direct image loading with credentials
  const containerStyle = {
    backgroundColor: bgColor === 'white' ? 'white' : 'black',
    display: 'inline-block',
    padding: '4px',
    borderRadius: '4px'
  };
  
  return (
    <div className="auth-image-wrapper" style={containerStyle}>
      <img src={src} alt={alt} className={className} onClick={onClick} />
    </div>
  );
}

function App() {
  // Projects & selection
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // Mode: "image", "multi", or "video"
  const [mode, setMode] = useState("image");

  // File inputs
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [multiFiles, setMultiFiles] = useState([]); // for multi‐image mode
  const [videoFile, setVideoFile] = useState(null);
  const [count, setCount] = useState(3);

  // Results
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Lightbox state
  const [selectedImage, setSelectedImage] = useState(null);
  // User useState hook
  const [user, setUser] = useState(null);
  // Editing state
  const [editingProject, setEditingProject] = useState(null);
  const [editingName, setEditingName] = useState("");
  
  // Frame player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [fps, setFps] = useState(24); // Frames per second
  const [frameSequence, setFrameSequence] = useState([]); // Combined frame sequence with types
  const [deletedFrames, setDeletedFrames] = useState([]); // Frames that have been removed
  const [draggedFrame, setDraggedFrame] = useState(null); // Currently dragged frame
  const [dragOverIndex, setDragOverIndex] = useState(null); // Drop target index
  
  // Background color state for images
  const [imageBgColor, setImageBgColor] = useState('white'); // 'black' or 'white'
  const [showBgColorMenu, setShowBgColorMenu] = useState(false); // Dropdown menu state
  
  // Download numbering state
  const [startNumber, setStartNumber] = useState(1);
  const [endNumber, setEndNumber] = useState(1);

  // Help dropdown state - open by default
  const [showHelpDropdown, setShowHelpDropdown] = useState(true);

  const freeCredits = user?.free_credits_remaining ?? user?.credits ?? 0;
  const hasActiveSubscription = Boolean(user?.has_active_subscription);
  const canUseInterpolation = Boolean(user && (hasActiveSubscription || freeCredits > 0));
  const interpolationBlockReason = !user
    ? "Sign in with Google to upload and interpolate."
    : !canUseInterpolation
      ? "You are out of free credits. An active subscription is required to interpolate."
      : "";

  function syncUserFromResponse(data) {
    if (data?.user) {
      setUser(data.user);
    }
  }

  function getApiErrorMessage(responseText) {
    try {
      const parsed = JSON.parse(responseText);
      return parsed.detail || parsed.message || responseText;
    } catch {
      return responseText;
    }
  }

  function initializeGoogleSignIn() {
    if (!window.google || !GOOGLE_CLIENT_ID) {
      return false;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });

    return true;
  }

  function promptGoogleSignIn() {
    setError("");

    if (!initializeGoogleSignIn()) {
      setError("Google sign-in is still loading. Please try again in a moment.");
      return;
    }

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        setError("Please sign in with Google before uploading or interpolating.");
      }
    });
  }

// this code block initializes Google auth and renders the header sign-in button
  useEffect(() => {
    if (window.google && !user) {
      const googleSigninDiv = document.getElementById("google-signin-div");
      if (googleSigninDiv) {
        googleSigninDiv.innerHTML = "";
        googleSigninDiv.style.display = "block";
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        window.google.accounts.id.renderButton(
          googleSigninDiv,
          { theme: "outline", size: "large" }
        );
      }
    } else if (window.google && user) {
      // Disable Google one-tap when user is logged in
      window.google.accounts.id.cancel();
      window.google.accounts.id.disableAutoSelect();
      
      // More aggressive cleanup - hide all Google elements
      const hideGoogleElements = () => {
        // First, clear the google-signin-div if it exists
        const googleDiv = document.getElementById('google-signin-div');
        if (googleDiv) {
          googleDiv.innerHTML = '';
          googleDiv.style.display = 'none';
        }
        
        const selectors = [
          '[data-client-id]',
          '.g_id_signin', 
          '#credential_picker_container',
          '[id*="credential"]',
          'div[role="button"][data-idom-class]',
          'div[jsaction*="click"]',
          '.VfPpkd-LgbsSe',
          'div[data-google]',
          '#google-signin-div',
          '[class*="g-signin"]',
          '[class*="g_id"]'
        ];
        
        selectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            el.style.display = 'none';
            el.remove(); // Actually remove the element
          });
        });
      };
      
      hideGoogleElements();
      
      // Run cleanup again after a short delay in case Google adds elements later
      setTimeout(hideGoogleElements, 100);
      setTimeout(hideGoogleElements, 500);
      setTimeout(hideGoogleElements, 1000);
    }
  }, [user]); 

  async function handleGoogleResponse(response) {
    const idToken = response.credential;
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: idToken }),
      });
      if (!res.ok) throw new Error("Login failed");
        const data = await res.json();
        setUser(data.user);
    } catch (e) {
      console.error(e);
    }
  }

  function handleLogout() {
    fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    setUser(null);
    
    // Clear user-specific data on logout
    setResults([]);
    setProjects([]);
    setSelectedProject(null);
    
    window.google.accounts.id.disableAutoSelect();
  }

  // code for credit and billing on profile click for stripe stuff
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownloadFrames, setSelectedDownloadFrames] = useState(new Set());

  // Handle subscription checkout
  async function handleSubscribe(priceId) {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Subscription error:', error);
      setError('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function goToBilling() {
    const res = await fetch(`${API_BASE}/billing`, {
      method: "GET",           // explicit, if you like clarity
      credentials: "include",  // so your auth cookie is sent
    });
    if (!res.ok) {
      console.error("Failed to fetch billing URL:", res.status, await res.text());
      throw new Error("Failed to fetch billing URL");
    }
    const { url } = await res.json();
    window.location.href = url;
  }

  // Removed duplicate handleDownloadFrames - using the ZIP download version below

  // Center Stripe checkout when it appears
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Look for Stripe checkout elements
              const stripeElements = node.querySelectorAll ? 
                node.querySelectorAll('[data-stripe], .stripe-checkout, .stripe-modal, iframe[src*="checkout.stripe.com"]') : [];
              
              stripeElements.forEach((element) => {
                element.style.position = 'fixed';
                element.style.top = '50%';
                element.style.left = '50%';
                element.style.transform = 'translate(-50%, -50%)';
                element.style.zIndex = '9999';
              });
              
              // Also check if the node itself is a Stripe element
              if (node.dataset && (node.dataset.stripe || node.classList.contains('stripe-checkout'))) {
                node.style.position = 'fixed';
                node.style.top = '50%';
                node.style.left = '50%';
                node.style.transform = 'translate(-50%, -50%)';
                node.style.zIndex = '9999';
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  // Check for existing user session and fetch projects on mount
  useEffect(() => {
    // Check if user is already authenticated
    fetch(`${API_BASE}/auth/me`, {
      credentials: "include"
    })
      .then((r) => {
        if (r.ok) {
          return r.json();
        }
        throw new Error("Not authenticated");
      })
      .then((data) => {
        setUser(data.user);
        
        // Fetch past projects only if user is authenticated
        return fetch(`${API_BASE}/projects`, {
          credentials: "include"
        });
      })
      .then((r) => {
        if (r && r.ok) {
          return r.json();
        }
        throw new Error("Cannot fetch projects");
      })
      .then((data) => setProjects(data.projects || []))
      .catch(() => {
        // User not authenticated or projects fetch failed
        setUser(null);
        setProjects([]);
      });
  }, []);

  // Fetch projects whenever user logs in
  useEffect(() => {
    if (user) {
      fetch(`${API_BASE}/projects`, {
        credentials: "include"
      })
        .then((r) => r.json())
        .then((data) => setProjects(data.projects || []))
        .catch(console.error);
    }
  }, [user]);

  // Build frame sequence when results change
  useEffect(() => {
    if (results.length > 0) {
      const newSequence = results.map((url, index) => ({
        id: `result-${index}`,
        type: 'result',
        url: url,
        originalIndex: index
      }));
      setFrameSequence(newSequence);
    } else {
      setFrameSequence([]);
    }
  }, [results]);

  // Get all frames for playback
  const allFrames = frameSequence.map(frame => frame.url);

  // Frame player effect
  useEffect(() => {
    if (isPlaying && allFrames.length > 0) {
      const interval = setInterval(() => {
        setCurrentFrameIndex((prevIndex) => 
          prevIndex >= allFrames.length - 1 ? 0 : prevIndex + 1
        );
      }, 1000 / fps); // Frame interval based on FPS
      
      return () => clearInterval(interval);
    }
  }, [isPlaying, allFrames.length, fps]);

  // Reset frame index when frame sequence changes
  useEffect(() => {
    setCurrentFrameIndex(0);
    setIsPlaying(false);
  }, [frameSequence]);

  function handleNewProject() {
    setSelectedProject(null);
    setResults([]);
    setFileA(null);
    setFileB(null);
    setMultiFiles([]);
    setVideoFile(null);
  }

  async function handleInterpolate() {
    setError("");

    if (!user) {
      promptGoogleSignIn();
      return;
    }

    if (!canUseInterpolation) {
      setError("You are out of free credits. Please subscribe to keep interpolating.");
      setShowSubscriptionModal(true);
      return;
    }

    setLoading(true);
    setResults([]);

    try {
      let response;
      if (mode === "image") {
        if (!fileA || !fileB) throw new Error("Please upload both images");
        const form = new FormData();
        form.append("frame0", fileA);
        form.append("frame1", fileB);
        form.append("count", count);
        response = await fetch(`${API_BASE}/interpolate`, {
          method: "POST",
          credentials: "include",
          body: form
        });
      } else if (mode === "multi") {
        if (multiFiles.length < 2) throw new Error("Please upload at least two images");
        const form = new FormData();
        multiFiles.slice(0, 10).forEach((file, idx) => {
          form.append(`images`, file);
        });
        form.append("count", count);
        response = await fetch(`${API_BASE}/interpolate-multi`, {
          method: "POST",
          credentials: "include",
          body: form
        });
      } else {
        if (!videoFile) throw new Error("Please upload a video file");
        const form = new FormData();
        form.append("video", videoFile);
        response = await fetch(`${API_BASE}/interpolate-video`, {
          method: "POST",
          credentials: "include",
          body: form
        });
      }

      if (!response.ok) {
        const err = await response.text();
        const message = getApiErrorMessage(err) || "Interpolation failed";
        if (response.status === 402) {
          setShowSubscriptionModal(true);
        }
        throw new Error(message);
      }
      const data = await response.json();
      syncUserFromResponse(data);
      setResults(data.frames || [data.video]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveToProject() {
    if (!user) {
      alert("Please sign in to save projects");
      return;
    }
    
    try {
      // Save to database
      const response = await fetch(`${API_BASE}/projects`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: new Date().toLocaleString(),
          frames: results
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to save project");
      }
      
      const savedProject = await response.json();
      
      // Create the project object for local state
      const newProject = {
        project_id: savedProject.project_id,
        created_at: savedProject.created_at,
        name: savedProject.name,
        frames: results
      };
      
      // Add to projects list
      setProjects([newProject, ...projects]);
      
      // Select the new project
      setSelectedProject(newProject.project_id);
    } catch (e) {
      console.error("Error saving project:", e);
      alert("Failed to save project. Please try again.");
    }
  }

  async function handleRenameProject(projectId, newName) {
    try {
      // Update in database
      const response = await fetch(`${API_BASE}/projects/${projectId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName })
      });
      
      if (!response.ok) {
        throw new Error("Failed to rename project");
      }
      
      // Update local state
      setProjects(projects.map(p => 
        p.project_id === projectId 
          ? { ...p, name: newName }
          : p
      ));
      setEditingProject(null);
    } catch (e) {
      console.error("Error renaming project:", e);
      alert("Failed to rename project. Please try again.");
    }
  }

  function handleFrameUpload(e, insertIndex) {
    const files = Array.from(e.target.files);
    const newFrames = files.map((file, i) => ({
      id: `custom-${Date.now()}-${i}`,
      type: 'custom',
      url: URL.createObjectURL(file),
      file: file
    }));
    
    const newSequence = [...frameSequence];
    newFrames.forEach((frame, i) => {
      newSequence.splice(insertIndex + i, 0, frame);
    });
    setFrameSequence(newSequence);
  }

  function removeFrame(frameId) {
    const frameToRemove = frameSequence.find(f => f.id === frameId);
    if (frameToRemove) {
      // Move to deleted frames instead of destroying
      if (frameToRemove.type === 'result') {
        setDeletedFrames([...deletedFrames, frameToRemove]);
      } else {
        // For custom frames, still clean up blob URL when permanently removing
        URL.revokeObjectURL(frameToRemove.url);
      }
      setFrameSequence(frameSequence.filter(f => f.id !== frameId));
    }
  }

  function restoreFrame(frameId, insertIndex) {
    const frameToRestore = deletedFrames.find(f => f.id === frameId);
    if (frameToRestore) {
      const newSequence = [...frameSequence];
      newSequence.splice(insertIndex, 0, frameToRestore);
      setFrameSequence(newSequence);
      setDeletedFrames(deletedFrames.filter(f => f.id !== frameId));
    }
  }

  function handleDragStart(e, frame) {
    setDraggedFrame(frame);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }

  function handleDragLeave() {
    setDragOverIndex(null);
  }

  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedFrame) {
      restoreFrame(draggedFrame.id, dropIndex);
      setDraggedFrame(null);
      setDragOverIndex(null);
    }
  }

  function clearDeletedFrames() {
    // Clean up any custom frame blob URLs before clearing
    deletedFrames.forEach(frame => {
      if (frame.type === 'custom') {
        URL.revokeObjectURL(frame.url);
      }
    });
    setDeletedFrames([]);
  }

  async function handleDownloadFrames() {
    if (selectedDownloadFrames.size === 0) {
      alert("Please select at least one frame to download");
      return;
    }

    try {
      // Show loading state
      const downloadButton = document.querySelector('.download-selected-btn');
      const originalText = downloadButton.textContent;
      downloadButton.textContent = 'Preparing download...';
      downloadButton.disabled = true;

      // Send request to backend to create ZIP file
      const selectedIndices = Array.from(selectedDownloadFrames).sort((a, b) => a - b);
      
      const response = await fetch(`${API_BASE}/download-frames`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          urls: results,
          indices: selectedIndices
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create download');
      }
      
      // Download the ZIP file
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `flipframe_frames_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Reset button state
      downloadButton.textContent = originalText;
      downloadButton.disabled = false;
      
      setShowDownloadModal(false);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download frames. Please try again.');
      
      // Reset button state on error
      const downloadButton = document.querySelector('.download-selected-btn');
      downloadButton.textContent = `Download Selected Frames (${selectedDownloadFrames.size})`;
      downloadButton.disabled = false;
    }
  }

  async function handleDeleteProject(projectId) {
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete project");
      }
      
      // Remove from local state
      setProjects(projects.filter(p => p.project_id !== projectId));
      
      // Clear selection if deleted project was selected
      if (selectedProject === projectId) {
        setSelectedProject(null);
        setResults([]);
      }
    } catch (e) {
      console.error("Error deleting project:", e);
      alert("Failed to delete project. Please try again.");
    }
  }

  return (
    <div className="app-container">
      {/* ─── Sticky Header ────────────────────────────────────────────── */}
      <header className="header">
        <div className="header-left">
          <h1 className="company-name">FlipFrame</h1>
          <span className="tagline">Create more with FlipFrame</span>
          <a href="mailto:support@flipframe.io" className="support-email">support@flipframe.io</a>

          {/* How to use FlipFrame button */}
          <div className="help-button-wrapper">
            <button
              className="help-button"
              onClick={() => setShowHelpDropdown(!showHelpDropdown)}
            >
              How to use FlipFrame
            </button>

            {/* Help Dropdown */}
            {showHelpDropdown && (
              <div className="help-dropdown">
                <button
                  className="help-dropdown-close"
                  onClick={() => setShowHelpDropdown(false)}
                >
                  ×
                </button>
                <div className="help-content">
                  {/* Demo Video */}
                  <div className="demo-video-container">
                    <h4>Watch a Demo:</h4>
                    <video
                      className="demo-video"
                      controls
                      preload="metadata"
                    >
                      <source src="/FlipFrameDemo.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <h3>Welcome to FlipFrame!</h3>
                  <p>FlipFrame is an AI-powered frame interpolation tool that creates smooth transitions between images or enhances your videos.</p>

                  <h4>How to use:</h4>
                  <ol>
                    <li><strong>Choose a mode:</strong> Select Image Input, Multi-Image, or Video Input</li>
                    <li><strong>Upload your files:</strong> Upload your start and end frames (Image mode), multiple images (Multi-Image mode), or a video file</li>
                    <li><strong>Set in-betweens:</strong> Choose how many frames to generate between your images</li>
                    <li><strong>Click Interpolate:</strong> Let FlipFrame create smooth transitions</li>
                    <li><strong>Preview & Edit:</strong> Use the frame player to preview your animation and edit the sequence</li>
                    <li><strong>Save or Download:</strong> Save your project for later or download your frames</li>
                  </ol>

                  <h4>Features:</h4>
                  <ul>
                    <li>Frame-by-frame editing and reordering</li>
                    <li>Adjustable playback speed (FPS)</li>
                    <li>Custom frame insertion</li>
                    <li>Background color selection</li>
                    <li>Selective frame downloading</li>
                  </ul>

                  <p><strong>Need help?</strong> Contact us at <a href="mailto:support@flipframe.io">support@flipframe.io</a></p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="header-right">
          {!user ? (
            <div id="google-signin-div" />
          ) : (
            <>
              <button
                className={`header-credit-pill ${!canUseInterpolation ? "locked" : ""}`}
                onClick={() => {
                  if (!canUseInterpolation) {
                    setShowSubscriptionModal(true);
                  }
                }}
                title={hasActiveSubscription ? "Subscription active" : "1 interpolation uses 1 free credit"}
              >
                <span>Free credits</span>
                <strong>{freeCredits}</strong>
                <small>{hasActiveSubscription ? "subscribed" : "left"}</small>
              </button>

              <div
                className="user-profile"
                onClick={() => setDropdownOpen(o => !o)}
              >
                <img
                  src={user.picture}
                  alt="Avatar"
                  width="32"
                  style={{ borderRadius: "50%", marginRight: "0.5rem" }}
                />
                <span>{user.name}</span>

                {dropdownOpen && (
                  <div className="profile-dropdown">
                    <div className="dropdown-item">
                      <strong>Free credits:</strong> {freeCredits}
                    </div>
                    <div className="dropdown-item">
                      <strong>Subscription:</strong> {hasActiveSubscription ? "Active" : "Inactive"}
                    </div>
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setShowSubscriptionModal(true);
                        setDropdownOpen(false);
                      }}
                    >
                      Subscribe
                    </div>
                    <div
                      className="dropdown-item billing-link"
                      onClick={goToBilling}
                    >
                      Billing
                    </div>
                    <div
                      className="dropdown-item"
                      onClick={handleLogout}
                    >
                      Logout
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
</header>?


      {/* ─── Sidebar + Main ───────────────────────────────────────────── */}
      <div className="layout">
        <aside className="sidebar">
          <img src={logo} alt="FlipFrame Logo" className="logo" />
              <h2>Projects</h2>
          <div className="projects-list">
            {projects.map(p => (
              <div
                key={p.project_id}
                className={
                  "project-item" +
                  (selectedProject === p.project_id ? " selected" : "")
                }
              >
                {editingProject === p.project_id ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => handleRenameProject(p.project_id, editingName)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameProject(p.project_id, editingName);
                      } else if (e.key === 'Escape') {
                        setEditingProject(null);
                      }
                    }}
                    className="rename-input"
                    autoFocus
                  />
                ) : (
                  <div className="project-row">
                    <div
                      onClick={() => {
                        setSelectedProject(p.project_id);
                        setResults(p.frames);
                      }}
                      onDoubleClick={() => {
                        setEditingProject(p.project_id);
                        setEditingName(p.name || new Date(p.created_at).toLocaleString());
                      }}
                      className="project-name"
                    >
                      <small>{p.name || new Date(p.created_at).toLocaleString()}</small>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(p.project_id);
                      }}
                      className="delete-project-btn"
                      title="Delete project"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <main className="main-panel">
          {/* Mode Toggle */}
          <div className="mode-toggle">
            <button
              className={mode === "image" ? "active" : ""}
              onClick={() => setMode("image")}
            >
              Image Input
            </button>
            <button
              className={mode === "multi" ? "active" : ""}
              onClick={() => setMode("multi")}
            >
              Multi-Image
            </button>
            <button
              className={mode === "video" ? "active" : ""}
              onClick={() => setMode("video")}
            >
              Video Input
            </button>
          </div>

          {/* Upload Section */}
          <div className="upload-section">
            {mode === "image" ? (
              <>
                <div className="file-input-wrapper">
                  <label className={`custom-file-label ${!user ? "disabled" : ""}`}>
                    {fileA ? fileA.name : "Upload Start Frame"}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={!user}
                      onChange={e => setFileA(e.target.files[0])}
                    />
                  </label>
                </div>

                <div className="file-input-wrapper">
                  <label className={`custom-file-label ${!user ? "disabled" : ""}`}>
                    {fileB ? fileB.name : "Upload End Frame"}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={!user}
                      onChange={e => setFileB(e.target.files[0])}
                    />
                  </label>
                </div>

                <div className="count-input">
                  <label># In-betweens</label>
                  <select
                    value={count}
                    onChange={e => setCount(Number(e.target.value))}
                    className="count-selector"
                  >
                    {[...Array(10)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : mode === "multi" ? (
              <>
                <div className="file-input-wrapper">
                  <label className={`custom-file-label ${!user ? "disabled" : ""}`}>
                    {multiFiles.length > 0
                      ? `${multiFiles.length} file(s) selected`
                      : "Upload 2–10 Images"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={!user}
                      onChange={e => {
                        const files = Array.from(e.target.files).slice(0, 10);
                        setMultiFiles(files);
                      }}
                    />
                  </label>
                </div>

                <div className="count-input">
                  <label># In-betweens per Pair</label>
                  <select
                    value={count}
                    onChange={e => setCount(Number(e.target.value))}
                    className="count-selector"
                  >
                    {[...Array(3)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className="file-input-wrapper">
                <label className={`custom-file-label ${!user ? "disabled" : ""}`}>
                  {videoFile ? videoFile.name : "Upload Video File"}
                  <input
                    type="file"
                    accept="video/*"
                    disabled={!user}
                    onChange={e => setVideoFile(e.target.files[0])}
                  />
                </label>
              </div>
            )}
            <button
              className="interpolate-btn"
              onClick={handleInterpolate}
              disabled={loading}
              title={interpolationBlockReason}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Processing...
                </>
              ) : (
                "Interpolate"
              )}
            </button>
            {user && interpolationBlockReason && <div className="gate-text">{interpolationBlockReason}</div>}
            {error && <div className="error-text">{error}</div>}
          </div>

          {/* Results Display */}
          <div className="results-display">
            {mode === "video" ? (
              <div className="video-container">
                {results.length > 0 ? (
                  <video
                    src={`${API_BASE}${results[0]}`}
                    controls
                    className="video-player"
                  />
                ) : (
                  <p className="placeholder-text">No video yet</p>
                )}
              </div>
            ) : (
              <>
                <div className="image-scroller">
                  {results.length > 0 ? (
                    results.map((url, i) => {
                      const fullUrl = `${API_BASE}${url}`;
                      return (
                        <AuthImage
                          key={i}
                          src={fullUrl}
                          alt={`frame ${i}`}
                          className="thumb"
                          onClick={() => setSelectedImage(fullUrl)}
                          bgColor={imageBgColor}
                        />
                      );
                    })
                  ) : (
                    <p className="placeholder-text">Output Window</p>
                  )}
                </div>
                {results.length > 0 && (
                  <>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative' }}>
                      <button 
                        className="save-to-project-btn"
                        onClick={handleSaveToProject}
                      >
                        Save to Projects
                      </button>
                      <button 
                        className="download-btn"
                        onClick={() => {
                          // Initialize with all frames selected
                          const allFrameIndices = new Set();
                          results.forEach((_, index) => allFrameIndices.add(index));
                          setSelectedDownloadFrames(allFrameIndices);
                          setShowDownloadModal(true);
                        }}
                      >
                        Download
                      </button>
                      <div className="bg-color-selector">
                        <button 
                          className="bg-color-btn"
                          onClick={() => setShowBgColorMenu(!showBgColorMenu)}
                        >
                          Background Color
                        </button>
                        {showBgColorMenu && (
                          <div className="bg-color-dropdown">
                            <button 
                              className={`bg-option ${imageBgColor === 'black' ? 'active' : ''}`}
                              onClick={() => {
                                setImageBgColor('black');
                                setShowBgColorMenu(false);
                              }}
                            >
                              Black
                            </button>
                            <button 
                              className={`bg-option ${imageBgColor === 'white' ? 'active' : ''}`}
                              onClick={() => {
                                setImageBgColor('white');
                                setShowBgColorMenu(false);
                              }}
                            >
                              White
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Frame Player */}
                    <div className="frame-player">
                      <div className="player-display">
                        {frameSequence.length > 0 && (
                          frameSequence[currentFrameIndex]?.type === 'result' ? (
                            <AuthImage
                              src={`${API_BASE}${frameSequence[currentFrameIndex].url}`}
                              alt={`Playing frame ${currentFrameIndex}`}
                              className="player-frame"
                              bgColor={imageBgColor}
                            />
                          ) : (
                            <img
                              src={frameSequence[currentFrameIndex]?.url}
                              alt={`Playing custom frame ${currentFrameIndex}`}
                              className="player-frame"
                            />
                          )
                        )}
                      </div>
                      
                      <div className="player-controls">
                        <button
                          className="play-pause-btn"
                          onClick={() => setIsPlaying(!isPlaying)}
                        >
                          {isPlaying ? '⏸️' : '▶️'}
                        </button>
                        
                        
                        <div className="fps-control">
                          <label>FPS:</label>
                          <select
                            value={fps}
                            onChange={(e) => setFps(parseInt(e.target.value))}
                            className="fps-selector"
                          >
                            <option value={6}>6</option>
                            <option value={12}>12</option>
                            <option value={18}>18</option>
                            <option value={24}>24</option>
                            <option value={30}>30</option>
                          </select>
                        </div>
                        
                      </div>
                      
                      {/* Frame Management Section */}
                      <div className="frame-management">
                        <h3>Frame Sequence Editor</h3>
                        
                        {/* Main Frame Sequence */}
                        <div className="frame-sequence">
                          {/* Insert button at the beginning */}
                          <div 
                            className={`insert-frame-wrapper ${dragOverIndex === 0 ? 'drag-over' : ''}`}
                            onDragOver={(e) => handleDragOver(e, 0)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, 0)}
                          >
                            <label className="insert-frame-btn">
                              <span>+</span>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleFrameUpload(e, 0)}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                          
                          {frameSequence.map((frame, i) => (
                            <React.Fragment key={frame.id}>
                              <div 
                                className={`sequence-frame ${frame.type === 'custom' ? 'custom' : ''} ${currentFrameIndex === i ? 'active' : ''}`}
                                onClick={() => setCurrentFrameIndex(i)}
                              >
                                {frame.type === 'result' ? (
                                  <AuthImage
                                    src={`${API_BASE}${frame.url}`}
                                    alt={`Frame ${i + 1}`}
                                    className="sequence-thumb"
                                    bgColor={imageBgColor}
                                  />
                                ) : (
                                  <img
                                    src={frame.url}
                                    alt={`Custom frame ${i + 1}`}
                                    className="sequence-thumb"
                                  />
                                )}
                                <span className="frame-number">{i + 1}</span>
                                <button
                                  className="remove-frame-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFrame(frame.id);
                                  }}
                                  title={frame.type === 'result' ? 'Remove frame (can be restored)' : 'Delete custom frame'}
                                >
                                  ×
                                </button>
                              </div>
                              
                              {/* Insert button after each frame */}
                              <div 
                                className={`insert-frame-wrapper ${dragOverIndex === i + 1 ? 'drag-over' : ''}`}
                                onDragOver={(e) => handleDragOver(e, i + 1)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, i + 1)}
                              >
                                <label className="insert-frame-btn">
                                  <span>+</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => handleFrameUpload(e, i + 1)}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                        
                        {/* Deleted Frames Section */}
                        {deletedFrames.length > 0 && (
                          <div className="deleted-frames-section">
                            <div className="deleted-frames-header">
                              <h4>Removed Frames</h4>
                              <button 
                                className="clear-deleted-btn"
                                onClick={clearDeletedFrames}
                                title="Permanently delete all removed frames"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="deleted-frames-container">
                              {deletedFrames.map((frame) => (
                                <div 
                                  key={frame.id}
                                  className="deleted-frame"
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, frame)}
                                >
                                  <AuthImage
                                    src={`${API_BASE}${frame.url}`}
                                    alt={`Deleted frame`}
                                    className="deleted-thumb"
                                    bgColor={imageBgColor}
                                  />
                                  <div className="deleted-frame-overlay">
                                    <span className="drag-hint">Drag to restore</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Lightbox modal */}
          {selectedImage && (
            <div
              className="modal-overlay"
              onClick={() => setSelectedImage(null)}
            >
              <AuthImage
                src={selectedImage}
                alt="Enlarged frame"
                className="modal-image"
                bgColor={imageBgColor}
              />
            </div>
          )}
        </main>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowDownloadModal(false)}
        >
          <div
            className="download-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Download Frames</h2>
              <button
                className="close-modal-btn"
                onClick={() => setShowDownloadModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="numbering-controls">
              <div className="total-frames">
                Total Available Frames: {results.length}
              </div>
              <div className="numbering-inputs">
                <div className="input-group">
                  <label htmlFor="startNumber">Starting Number:</label>
                  <input
                    id="startNumber"
                    type="number"
                    min="1"
                    value={startNumber}
                    onChange={(e) => setStartNumber(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="endNumber">Ending Number:</label>
                  <input
                    id="endNumber"
                    type="number"
                    min="1"
                    value={endNumber}
                    onChange={(e) => setEndNumber(parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
            </div>
            
            <div className="download-frames-scrollable">
              {results.map((url, index) => {
                const isSelected = selectedDownloadFrames.has(index);
                
                return (
                  <div
                    key={index}
                    className={`download-frame ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      const newSelected = new Set(selectedDownloadFrames);
                      if (isSelected) {
                        newSelected.delete(index);
                      } else {
                        newSelected.add(index);
                      }
                      setSelectedDownloadFrames(newSelected);
                    }}
                  >
                    <AuthImage
                      src={`${API_BASE}${url}`}
                      alt={`Frame ${index + 1}`}
                      className="download-thumb"
                      bgColor={imageBgColor}
                    />
                    <div className="frame-label">
                      Frame {index + 1}
                    </div>
                    {isSelected && <div className="check-mark">✓</div>}
                  </div>
                );
              })}
            </div>
            
            <div className="download-actions">
              <button
                className="select-all-btn"
                onClick={() => {
                  const allIndices = new Set();
                  results.forEach((_, index) => allIndices.add(index));
                  setSelectedDownloadFrames(allIndices);
                }}
              >
                Select All
              </button>
              <button
                className="deselect-all-btn"
                onClick={() => setSelectedDownloadFrames(new Set())}
              >
                Deselect All
              </button>
              <button
                className="download-selected-btn"
                onClick={handleDownloadFrames}
                disabled={selectedDownloadFrames.size === 0}
              >
                Download Selected Frames ({selectedDownloadFrames.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSubscriptionModal(false)}
        >
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Choose Your Plan</h2>
              <button
                className="close-modal-btn"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="custom-pricing-container">
              <div className="pricing-plans">
                <div className="pricing-plan">
                  <div className="plan-header">
                    <h3>Monthly Plan</h3>
                    <div className="plan-price">
                      <span className="price">$9.99</span>
                      <span className="period">/month</span>
                    </div>
                  </div>
                  <div className="plan-features">
                    <ul>
                      <li>Unlimited frame interpolation</li>
                      <li>High-quality video processing</li>
                      <li>Project saving & management</li>
                      <li>Priority support</li>
                    </ul>
                  </div>
                  <button 
                    className="subscribe-btn monthly"
                    onClick={() => handleSubscribe('price_1QdW4VHohDCnb2A5oQW6HZx9')}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Subscribe Monthly'}
                  </button>
                </div>
                
                <div className="pricing-plan popular">
                  <div className="popular-badge">Most Popular</div>
                  <div className="plan-header">
                    <h3>Annual Plan</h3>
                    <div className="plan-price">
                      <span className="price">$99.99</span>
                      <span className="period">/year</span>
                    </div>
                    <div className="savings">Save 17%</div>
                  </div>
                  <div className="plan-features">
                    <ul>
                      <li>Everything in Monthly Plan</li>
                      <li>2 months free</li>
                      <li>Priority processing</li>
                      <li>Advanced export options</li>
                    </ul>
                  </div>
                  <button 
                    className="subscribe-btn annual"
                    onClick={() => handleSubscribe('price_1QdW4VHohDCnb2A5oQW6HZx9')}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Subscribe Annually'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default App;

  
  
  
  
  
  
